import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { apiRequest } from "../../lib/request"
import {
  normalizeMediaStatsRows,
  buildMediaStatsQueryString,
  groupMediaStatsRowsByClient,
} from "../../lib/mediaStatsApi"
import { deleteMedia } from "./mediaDeleteSlice"

function resolveClientIds(arg = {}) {
  if (Array.isArray(arg.clientIds) && arg.clientIds.length > 0) {
    return arg.clientIds
  }
  if (arg.clientId != null && arg.clientId !== "") {
    return [arg.clientId]
  }
  return []
}

/**
 * GET /media/stats — single `clientId` or multiple `clientIds`.
 * Normalizes rows and merges into `state.statsByClient` by client id.
 */
export const fetchMediaStats = createAsyncThunk(
  "mediaStats/fetch",
  async (arg = {}, { rejectWithValue }) => {
    try {
      const clientIds = resolveClientIds(arg)
      if (clientIds.length === 0) {
        return rejectWithValue("clientId or clientIds is required")
      }

      const qs = buildMediaStatsQueryString(clientIds)
      const response = await apiRequest(`/media/stats${qs}`, {
        method: "GET",
      })

      const rows = normalizeMediaStatsRows(
        response?.data !== undefined ? response.data : response,
      )
      const statsByClient = groupMediaStatsRowsByClient(rows)

      return { clientIds, statsByClient }
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load stats")
    }
  },
  {
    condition: (arg = {}, { getState }) => {
      if (arg.force) return true
      const clientIds = resolveClientIds(arg)
      if (clientIds.length === 0) return false
      const slice = getState().mediaStats ?? {}
      if (slice.loading) return false
      const fetched = slice.fetchedClientIds || {}
      return !clientIds.every((id) => fetched[String(id)])
    },
  },
)

/** @deprecated Prefer `fetchMediaStats` — same thunk; alias for org-settings imports. */
export const fetchOrgMediaStats = fetchMediaStats

const mediaStatsSlice = createSlice({
  name: "mediaStats",
  initialState: {
    statsByClient: {},
    fetchedClientIds: {},
    loading: false,
    error: null,
  },
  reducers: {},

  extraReducers: (builder) => {
    builder
      .addCase(fetchMediaStats.pending, (state) => {
        state.loading = true
        state.error = null
      })

      .addCase(fetchMediaStats.fulfilled, (state, action) => {
        state.loading = false

        const { statsByClient, clientIds } = action.payload
        if (statsByClient && typeof statsByClient === "object") {
          for (const [key, data] of Object.entries(statsByClient)) {
            state.statsByClient[String(key)] = Array.isArray(data)
              ? data
              : normalizeMediaStatsRows(data)
          }
        }
        if (!state.fetchedClientIds) state.fetchedClientIds = {}
        ;(clientIds || []).forEach((id) => {
          state.fetchedClientIds[String(id)] = true
        })
      })

      .addCase(fetchMediaStats.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(deleteMedia.fulfilled, (state, action) => {
        const clientId = action.meta?.arg?.clientId
        if (clientId != null && state.fetchedClientIds) {
          delete state.fetchedClientIds[String(clientId)]
        }
      })
  },
})

export default mediaStatsSlice.reducer
